import {
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    Image,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useGlobalState
} from "../../../context";
import {
    useNavigation,
    useRoute
} from "@react-navigation/native";
import type RootStackParamList from "../../../types/navigation";
import type CommonStackParamList from "../../../types/navigation/common";
import type {
    RouteProp
} from "@react-navigation/native";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    RESTService
} from "../../../services/restAPI";
import OneSignalService from "../../../services/OneSignalService";
import GMKonyaLogoDark from "../../../assets/images/gmkonyalogodark.png";
import GMKonyaLogo from "../../../assets/images/gmkonyalogo.png";
import {
    storage
} from "../../../storage";

import ApplyForm from "../../app/applyForm";
import GuestApplyForm from "../../guest/guestApplyForm";

const FormRouter = () => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const route = useRoute<RouteProp<CommonStackParamList, "FormRouter">>();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        activeTheme,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        isGuestConfirmed,
        setIsGuestConfirmed
    ] = useState(false);

    const [
        globalState,
        setGlobalState
    ] = useGlobalState();

    useEffect(() => {
        if(!globalState.isAppReady) {
            const refreshToken = storage.getString("refreshToken");
            if(refreshToken) {
                RESTService.action("Me", null)
                    .then((res) => {
                        const userInfo = res.payload;
                        setGlobalState({
                            ...globalState,
                            isAppReady: true,
                            isGuest: false,
                            user: {
                                profilePhotoURL: userInfo.profilePhoto,
                                companyTitle: userInfo.companyTitle,
                                companyName: userInfo.companyName,
                                phoneNumber: userInfo.phoneNumber,
                                firstName: userInfo.firstName,
                                lastName: userInfo.lastName,
                                fullName: userInfo.fullName,
                                userType: userInfo.userType,
                                company: userInfo.company,
                                userID: userInfo.userID,
                                mail: userInfo.mail
                            }
                        });

                        OneSignalService.login(res.payload.userID);
                        OneSignalService.setTags({
                            userID: res.payload.userID
                        });
                    })
                    .catch(() => {
                        OneSignalService.logout();
                        storage.clearAll();
                        setGlobalState({
                            ...globalState,
                            isAppReady: true,
                            isGuest: true,
                            user: null
                        });
                    });
            } else {
                setGlobalState({
                    ...globalState,
                    isAppReady: true,
                    isGuest: true,
                    user: null
                });
            }
        }
    }, [
        globalState.isAppReady
    ]);

    useEffect(() => {
        if(!globalState.isAppReady) {
            return;
        }

        const formID = route.params?.formID;

        if(!formID) {
            if(navigation.canGoBack()) {
                navigation.goBack();
            }
        }
    }, [
        globalState.isAppReady,
        navigation,
        route
    ]);

    // Uygulama henüz başlatılmadıysa veya state hazır değilse loading
    if(!globalState.isAppReady) {
        return <PageContainer
            style={[
                stylesheet.container,
                stylesheet.loadingContainer
            ]}
            backgroundColor="default"
        >
            <ActivityIndicator
                size="large"
                color={colors.content.text.high}
            />
        </PageContainer>;
    }

    const formID = route.params?.formID;
    if(!formID) {
        return null; // ID yoksa boş
    }

    const refreshToken = storage.getString("refreshToken");
    const isUserLoggedIn = Boolean(globalState.user && refreshToken && !globalState.isGuest);

    // EĞER KULLANICI GİRİŞ YAPMIŞSA VE MİSAFİR DEĞİLSE
    if(isUserLoggedIn) {
        // Herhangi bir yönlendirme yapmadan, direkt App formunu render ediyoruz! (Bugsuz)
        return <ApplyForm />;
    }

    // EĞER KULLANICI AÇIKÇA MİSAFİR OLARAK DEVAM ETMEYİ SEÇTİYSE
    if(isGuestConfirmed) {
        // Direkt Guest formunu render ediyoruz!
        return <GuestApplyForm />;
    }

    const navigateToGuest = () => {
        setIsGuestConfirmed(true);
    };

    const navigateToLogin = () => {
        navigation.replace("Auth", {
            params: {
                redirectParams: {
                    formID: route.params?.formID
                },
                redirectRoute: "ApplyForm"
            },
            screen: "Login"
        } as never);
    };

    return <PageContainer
        style={[
            stylesheet.container,
            {
                padding: spaces.spacingLg
            }
        ]}
        backgroundColor="default"
    >
        <Image
            resizeMode="contain"
            source={activeTheme === "dark" ? GMKonyaLogoDark : GMKonyaLogo}
            style={[
                stylesheet.logo,
                {
                    marginTop: spaces.spacingXl * 2,
                    marginBottom: spaces.spacingLg
                }
            ]}
        />
        <View
            style={stylesheet.contentContainer}
        >
            <Text
                style={[
                    stylesheet.title,
                    {
                        marginBottom: spaces.spacingMd
                    }
                ]}
                variant="titleMediumSize"
            >
                {localize("fillForm")}
            </Text>
            <Text
                style={[
                    stylesheet.description,
                    {
                        marginBottom: spaces.spacingLg
                    }
                ]}
                variant="bodyMediumSize"
            >
                {localize("formRouterGuestDescription")}
            </Text>
            <View
                style={[
                    stylesheet.buttonContainer,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Button
                    spreadBehaviour="stretch"
                    onPress={navigateToLogin}
                    title={localize("login")}
                    size="large"
                />
                <Button
                    spreadBehaviour="stretch"
                    title={localize("continue-without-sign-up")}
                    onPress={navigateToGuest}
                    color="secondary"
                    size="large"
                />
            </View>
        </View>
    </PageContainer>;
};

export default FormRouter;
